"use strict";

// ---------------------------------------------------
// Dependencies

var SoaModel = require('bunsen/models/SoaModel.js');
var root = typeof window === 'undefined' ? global : window;

root.bunsen = root.bunsen || {};
/**
 * Backbone model describing a 1stdibs product.
 * @typedef {object} ItemModel
 */
/* eslint-disable vars-on-top */
var Item = root.bunsen.Item = module.exports =  SoaModel.extend();

var SoaCollection = require('bunsen/collections/SoaCollection.js');
var Items = require('bunsen/collections/inventory/Items');
var Backbone = require('dibs-backbone');
var $ = require('dibs-jquery-shim');
var _ = require('underscore');
var assert = require('bunsen/helpers/assert');
var Seller = require('bunsen/models/Seller');
// Leave this named `ImageModel` since the html5 spec has a new `Image` global
var ImageModel = require('bunsen/models/inventory/Image');
var Photo = require('bunsen/models/inventory/Photo');
var endpoints = require('dibs-endpoints');
var ShipmentQuotes = require('bunsen/collections/logistics/ShipmentQuotes');
var ItemAttributesModel = require('bunsen/models/inventory/ItemAttributes');
var ItemAttributesCollection = require('bunsen/collections/inventory/ItemAttributes');
var ItemCreator = require('bunsen/models/link-models/ItemCreator.js');
var ItemCreators = require('bunsen/collections/inventory/ItemCreators.js');
var StorefrontPreferences = require('bunsen/models/inventory/StorefrontPreferences');
var ReturnPolicy = require('bunsen/models/identity/ReturnPolicy');
var Condition = require('bunsen/models/inventory/Condition');
var Party = require('bunsen/models/Party');
var Mailer = require('bunsen/models/inventory/Mailer');
var legacyId = require('bunsen/helpers/legacyId');
var Clump = require('bunsen/libraries/clump');
var ItemCategories = require('bunsen/collections/inventory/ItemCategories');
var Category = require('./Category');
var ItemPricing = require('bunsen/models/inventory/ItemPricing');
var transitionMixin = require('bunsen/models/mixins/transitionMixin');
var shard = require('bunsen/helpers/shard');
var verticals = require('dibs-vertical-helpers');
var FinancialAmount = require('bunsen/models/FinancialAmount');
var Measurement = require('bunsen/models/inventory/Measurement');
var Holds = require('bunsen/collections/inventory/Holds');

// ---------------------------------------------------
// Local Vars

var HasOne = Backbone.One;
var HasMany = Backbone.Many;
var getDefaults;
var relations;
/* eslint-disable vars-on-top */

// graphql transforms all Y/N's into true/false
// make sure we can accomodate both
function isYes(prop) {
    return prop === 'Y' || prop === true;
}

// ---------------------------------------------------
// Module Definition

getDefaults = function () {
    var currentSeller = Seller.getCurrentSeller();
    var defaults;

    defaults = {
        creators: [],
        seller: {},
        shipmentQuotes: []
    };

    if (currentSeller && currentSeller.get('id')) {
        defaults.seller.id = currentSeller.get('id');
        defaults.apiType = Item.getApiType(currentSeller.getVerticalFromID());
    }

    return defaults;
};

relations = [
    {
        key: 'creators',
        relatedModel: ItemCreator,
        type: HasMany,
        collectionType: ItemCreators
    },
    {
        key: 'classification',
        type: HasOne,
        relatedModel: SoaModel.extend({
            relations: [{
                key: 'style',
                type: HasOne,
                relatedModel: SoaModel
            }, {
                key: 'categories',
                type: HasMany,
                collectionType: ItemCategories
            }]
        })
    },
    {
        key : 'attributes',
        relatedModel: ItemAttributesModel,
        type: HasOne
    },
    {
        key: 'materialsTechniques',
        collectionType: ItemAttributesCollection,
        type: HasMany,
        omitFromSave: true
    },
    {
        key: 'measurement',
        relatedModel: Measurement,
        type: HasOne
    },
    {
        key: 'shipmentQuotes',
        collectionType: ShipmentQuotes,
        type: HasMany,
        omitFromSave: true
    },
    {
        key: 'pricing',
        relatedModel: ItemPricing,
        type: HasOne
    },
    {
        key: 'condition',
        relatedModel: Condition,
        type: HasOne
    },
    {
        key: 'publishOptions',
        type: HasOne,
        relatedModel: SoaModel
    },
    {
        key: 'customMaterial',
        type: HasOne,
        relatedModel: SoaModel
    },
    {
        key: 'updateRestrictionList',
        type: HasOne,
        relatedModel: SoaModel
    },
    {
        key: 'store',
        type: HasOne,
        relatedModel: SoaModel
    },
    {
        key: 'seller',
        relatedModel: Seller,
        type: HasOne
    },
    {
        key: 'images',
        relatedModel: ImageModel,
        type: HasMany
    },
    {
        type: HasMany,
        key: 'photos',
        relatedModel: Photo
    },
    {
        key: 'returnPolicy',
        relatedModel: ReturnPolicy,
        type: HasOne
    },
    {
        key: 'status',
        relatedModel: SoaModel,
        type: HasOne
    },
    {
        key: 'mailer',
        type: HasOne,
        relatedModel: Mailer
    },
    {
        key: 'storefrontPreferences',
        type: HasOne,
        relatedModel: StorefrontPreferences
    },
    {
        key: 'versions',
        type: Backbone.Many,
        collectionType: SoaCollection.extend({
            model: SoaModel.extend({
                relations: [{
                    key: 'price',
                    type: HasOne,
                    relatedModel: FinancialAmount
                }]
            })
        })
    },
    {
        key: 'holds',
        type: HasMany,
        collectionType: Holds
    }
];

_.extend(Item.prototype, {
    getLegacyId : legacyId.modelHelper,

    defaults: getDefaults,
    relations: relations,
    // set the iid of the item so that Backbone knows this item is not new (see the .isNew() method) - hence,
    // Backbone will use a PUT create instead of a POST, triggering an "update" request, and not a "create" if the
    // item has an iid.
    idAttribute: "id",
    /**
     * On init, parse the item id. Depending on the source, the item ID may be prepended with a single letter
     * definint the vertical, eg. f_123 for item 123 in "furniure"
     * @param d
     */
    // save a copy of techniques during view switching
    savedTechniques: [],
    //Used in Image Upload
    verticalPrefixes : verticals.prefixMap,

    // In Backbone JS - the parse function is automatically called whenever you get model data back from the server. It
    // should return a hash map of attributes to be set on the model. In our case, we use 2 different end points for
    // fetch and save, so this function figures out which endpoint was hit, and grabs the correct data:
    parse: function parseItemModel(data) {
        var getDefaultsMap = {
            fashion: Item.getFashionDefaults,
            jewelry: Item.getJewelryDefaults,
            art: Item.getArtDefaults
        };

        data = SoaModel.prototype.parse.apply(this, arguments);

        if (data) {
            if (data.vertical && getDefaultsMap[data.vertical]) {
                data = $.extend(true, {}, getDefaultsMap[data.vertical](), data);
            }
        }
        return data;
    },

    /**
     * set a publish option to 'Y' and all others to 'N'
     * @param option
     * @param value
     */
    setOnePublishOption: function (option) {
        if (!_.contains(Item.PUBLISH_OPTION, option)) {
            throw Error('Invalid publishOption "' + option + '"');
        }
        _.each(Item.PUBLISH_OPTION, function (opt) {
            this.setPublishingOption(opt, false);
        }, this);

        this.setPublishingOption(option, true);
    },

    /**
     * set a publish option to 'Y' and all others to 'N'
     * @param option
     * @param value
     */
    setPublishOption: function (option) {
        if (Item.publishOptions.indexOf(option) === -1) {
            throw Error('Invalid publishOption "' + option + '"');
        }
        Item.publishOptions.forEach(_.bind(function (opt) {
            this.set('publishOptions.' + opt, 'N');
        }, this));

        this.set('publishOptions.' + option, 'Y');
    },

    /**
     * Save an item
     * Retry hack because of vertical confusion between solr and service model.  This cannot be used with clump because
     * there is no way to make a retry clump that is aware of all other items in the collection (we shouldn't know
     * about collections in the model here) and then call clump.run again.
     * @returns {Promise}
     */
    save: function (attrs, options) {
        var promise = $.Deferred();
        var self = this;
        var args = arguments;
        var retryVerticals = new RegExp(verticals.VERTICAL_JEWELRY + '|' + verticals.VERTICAL_FASHION);
        var save = Item.__super__.save.apply(this, arguments);
        options = options  || {};

        if (this.isNew()) {
            return save;
        }

        save.fail(function (jqXHR) {
            // retry if it's a 500, a retry vertical, and not in a clump
            // retries with clumps are handled in the Items collection saveAll method
            if (jqXHR.status === 500 && retryVerticals.test(self.get('vertical')) && !options.clump) {
                var prevVertical = self.get('vertical');
                var prevApiType = self.get('apiType');
                self.set('vertical', prevVertical === verticals.VERTICAL_FASHION ? verticals.VERTICAL_JEWELRY : verticals.VERTICAL_FASHION);
                self.set('apiType', self.get('vertical') + 'Item');

                Item.__super__.save.apply(self, args)
                    .then(_.bind(promise.resolve, promise))
                    .fail(_.bind(promise.reject, promise));

                self.set('vertical', prevVertical);
                self.set('apiType', prevApiType);
            }
            else {
                promise.reject.apply(promise, arguments);
            }
        })
        .then(_.bind(promise.resolve, promise));

        return promise;
    },

    // Methods from 1stdibs.com Item model:
    getIntegerId : function () {
        var id = this.get('id') || '0';
        return parseInt(String(id).replace(/\D+/g, ''), 10);
    },
    getAmountPerItem : function (currency) {
        var quantity = parseInt(this.get('pieces') || 0, 10);
        var pricing = this.get('pricing');
        var amount;

        if (!pricing) {
            return undefined;
        }

        amount = pricing.getTotalAmount(currency);
        if (this.isSoldIndividually()) {
            return amount / quantity;
        }
        return amount;
    },
    canMultipleQuantityBePurchased: function () {
        return isYes(this.get('pricing.pricePerPiece')) && this.get('pieces') > 1;
    },
    isSubmittedListing: function () {
        return this.isSubmittedToCategoryListing() || this.isSubmittedToFeaturedListing();
    },
    isSubmittedToCategoryListing: function () {
        return this.get('status.publishingStatus') === Item.statuses.SUBMITTED_TO_CATEGORY_SEARCH;
    },
    isSubmittedToFeaturedListing: function () {
        return this.get('status.publishingStatus') === Item.statuses.SUBMITTED_TO_NEW_LISTINGS;
    },
    isStorefrontListing: function () {
        return this.get('status.publishingStatus') === Item.statuses.STOREFRONT_ONLY;
    },
    isCategoryListing: function () {
        return this.get('status.publishingStatus') === Item.statuses.PUBLISHED_TO_CATEGORY_SEARCH;
    },
    isFeaturedListing: function () {
        return this.get('status.publishingStatus') === Item.statuses.PUBLISHED_TO_NEW_LISTINGS;
    },
    isOnHoldListing: function () {
        return this.get('status.publishingStatus') === Item.statuses.ON_HOLD;
    },
    isSoldListing: function () {
        return this.get('status.publishingStatus') === Item.statuses.SOLD;
    },
    isAddTextListing: function () {
        return this.get('status.publishingStatus') === Item.statuses.NEW;
    },
    isWorkInProgressListing: function () {
        return this.get('status.publishingStatus') === Item.statuses.WORK_IN_PROGRESS;
    },
    isUnpublishedListing: function () {
        return this.get('status.publishingStatus') === Item.statuses.UNPUBLISHED;
    },
    isPublishedListing: function () {
        return this.isStorefrontListing() ||
            this.isCategoryListing() ||
            this.isFeaturedListing();
    },
    isRejectedListing: function () {
        return this.get('status.publishingStatus') === Item.statuses.REJECTED;
    },
    isQueuedForCategoryListing: function () {
        return this.get('status.publishingStatus') === Item.statuses.QUEUED_FOR_CATEGORY_SEARCH;
    },
    isQueuedForFeaturedListing: function () {
        return this.get('status.publishingStatus') === Item.statuses.QUEUED_FOR_NEW_LISTINGS;
    },
    isQueuedListing: function () {
        return this.isQueuedForCategoryListing() || this.isQueuedForFeaturedListing();
    },
    isTradeReturnAvailable: function () {
        return (this.isFurniture() || this.isArt()) &&
            this.getConvertedPriceAmount(FinancialAmount.CURRENCY_USD) <= 35000 &&
            this.get('seller.shippingAddress.country') === 'US';
    },
    isPriceUponRequest: function () {
        return this.hidePrice() || this.getAmountIn(FinancialAmount.CURRENCY_USD) === 0;
    },
    canMoveToPending: function () {
        return this.isItemUnlisted() || this.isQueuedListing() || this.isSubmittedListing();
    },
    canMarkOnHold: function () {
        return this.isPublishedListing() || this.isQueuedListing() || this.isSubmittedListing();
    },
    canMarkDeleted: function () {
        return this.isAddTextListing() || this.isWorkInProgressListing() || this.isUnpublishedListing();
    },
    //workflow complete or unavailable, where is this used? check with finding about PDP
    canMarkSold: function () {
        return this.isPublishedListing() ||
            this.isOnHoldListing() ||
            this.isQueuedListing() ||
            this.isSubmittedListing();
    },
    dealerSuspended: function () {
        return this.get('seller') && this.get('seller').isStatusSuspended();
    },
    dealerClosed: function () {
        return this.get('seller') && this.get('seller').isStatusClosed();
    },
    dealerPending: function () {
        return this.get('seller') && this.get('seller').isStatusPending();
    },
    /**
     * @deprecated
     * @returns {*|boolean}
     */
    hidePrice: function () {
        return this.isPriceHidden();
    },
    isPriceHidden: function () {
        var price = this.getPrice();
        return (this.get('pricing') && isYes(this.get('pricing.hidePrice'))) || !price || price < 0.01;
    },
    isPriceHiddenByType: function (type) {
        var price = this.getPriceByType(type);
        return (price && isYes(price.get('hidePrice'))) || !price || price < 0.01;
    },
    canMakePurchase:  function (transaction) {
        var buyerCanAcceptPrivateOffer = transaction && transaction.buyerCanAcceptPrivateOffer();
        var canPurchaseOnHoldItem = this.isItemOnHold() && buyerCanAcceptPrivateOffer;
        return (
            (!this.isItemOnHold() || canPurchaseOnHoldItem) &&
            !this.isItemDeleted() &&
            !this.dealerSuspended() &&
            !this.dealerClosed() &&
            !this.dealerPending() &&
            !this.isItemSold() &&
            (!this.hidingPrice() || buyerCanAcceptPrivateOffer) &&
            !this.isItemUnlisted()
        );
    },
    canMakeBuyerOffer: function () {
        return this.isNegotiablePrice() &&
            !this.isItemSold() &&
            !this.isItemOnHold() &&
            !this.hidingPrice() &&
            !this.dealerSuspended() &&
            !this.isItemDeleted();
    },
    getDisplayStatus: function () {
        var status = this.get('status.publishingStatus');
        return Item.displayStatuses[status];
    },
    getDealerDisplayStatus: function () {
        var status = this.get('status.publishingStatus');
        var displayStatus;
        if (this.isArt()) {
            displayStatus = Item.artDisplayStatuses[status];
        }
        if (!displayStatus) {
            displayStatus = Item.displayStatuses[status];
        }
        return displayStatus;
    },
    /**
     * Returns the string for statuses that should show when price should
     * be hidden. Returns null otherwise.
     */
    getItemHidePriceStatus: function () {
        if (this.isItemSold()) {
            return Item.displayStatuses.SOLD;
        } else if (this.isItemOnHold()) {
            return Item.displayStatuses.ON_HOLD;
        } else if (this.isPriceUponRequest()) {
            return 'Price Upon Request';
        }
        return null;
    },
    /**
     * proxies call localizedAmount out to pricing.totalAmount when present
     */
    localizedAmount: function (options) {
        var totalAmount = this.get('pricing.totalAmount');
        if (totalAmount) {
            return totalAmount.localizedAmount(options);
        }
    },
    getConvertedPriceAmount: function (currency) {
        var prop = 'pricing.totalAmount.convertedAmounts.' + currency;
        return this.get(prop);
    },
    getCurrency: function () {
        return this.get('pricing.totalAmount.currency');
    },
    getPrice: function () {
        return this.get('pricing.totalAmount.amount');
    },
    getPriceByType: function (type) {
        var pricing = this.get('pricing');
        return pricing && pricing.getPriceByType(type);
    },
    getPriceIn: function (type, currency) {
        var price = this.getPriceByType(type);
        var amount;
        currency = currency || 'USD';
        if (price) {
            // FinancialAmount model
            amount = price.get('amount');
            if (amount) {
                return amount.getAmountIn(currency);
            }
        }
        // return null so that it's easy
        // to detect whether or not there is a price
        return null;
    },
    getConvertedPriceAmounts: function (type) {
        var prices = this.getPriceByType(type);
        return prices ? prices.get('amount.convertedAmounts') : null;
    },
    hidingPrice: function () {
        var price = this.getPrice();
        return !price || price < 0.01 || this.hidePrice();
    },
    getNumberOfPieces: function () {
        return this.get('pieces');
    },
    /**
     * use isSoldIndividually method
     * @deprecated
     * @returns {*}
     */
    soldIndividually: function () {
        return this.isSoldIndividually();
    },
    fetchKeyFields: function (options) {
        var self = this;

        options = options || {};
        options.data = _.extend({}, {
            fields: 'key',
            shipmentQuotesView: 'PDP'
        }, options.data);

        /* eslint-disable camelcase */
        if (!this._fetchKeyFields_P) {
            this._fetchKeyFields_P = this.fetch(options).fail(function () {
                self._fetchKeyFields_P = null;
            });
        }
        return this._fetchKeyFields_P;
        /* eslint-disable camelcase */
    },
    _isVertical: function (vertical) {
        return this.get('vertical') === vertical;
    },
    isArt: function () {
        return this._isVertical(verticals.VERTICAL_ART);
    },
    isFashion: function () {
        return this._isVertical(verticals.VERTICAL_FASHION);
    },
    isFurniture: function () {
        return this._isVertical(verticals.VERTICAL_FURNITURE);
    },
    isJewelry: function () {
        return this._isVertical(verticals.VERTICAL_JEWELRY);
    },
    //Should be used on POST only. Should already be on the model during a PUT
    getApiType: function (vertical) {
        return Item.getApiType(vertical);
    },
    setApiType: function (vertical) {
        this.set('apiType', this.getApiType(vertical));
    },
    getTotalAmounts: function () {
        return this.get('pricing.totalAmount.convertedAmounts');
    },
    getPriceLocksByType: function (type) {
        var pricing = this.get('pricing');
        return pricing.getPriceLockByType(type);
    },
    getPriceLocksIn: function (type, currency) {
        var priceLock = this.getPriceLocksByType(type);
        var amount;
        currency = currency || 'USD';
        if (priceLock) {
            // FinancialAmount model
            amount = priceLock.getOriginalPriceIn(currency);
            if (amount) {
                return amount;
            }
        }
        // return null so that it's easy
        // to detect whether or not there is a price
        return null;
    },
    getInitialPriceAmounts: function () {
        return this.get('pricing.initialPrice.convertedAmounts');
    },
    getAmountIn: function (currency) {
        return this.get('pricing.totalAmount').getAmountIn(currency);
    },
    isNegotiablePriceByType: function (type) {
        var price = this.getPriceByType(type);
        if (price) {
            return isYes(price.get('negotiable'));
        }
        return null;
    },
    isNegotiablePrice: function () {
        return isYes(this.get('pricing.negotiable'));
    },
    isBargainPrice: function () {
        return isYes(this.get('pricing.bargain'));
    },
    isNewListing: function () {
        return isYes(this.get('status.newListing'));
    },
    isSaturdaySale: function () {
        return isYes(this.get('status.inSaturdaySale'));
    },
    isAvailable: function () {
        return isYes(this.get('status.isAvailable'));
    },
    isItemMarketplaceEver: function () {
        return this.getPublishingOption(Item.PUBLISH_OPTION.MARKETPLACE);
    },
    isItemMarketplace: function () {
        return this.getPublishingOption(Item.PUBLISH_OPTION.MARKET);
    },
    isItemStorefront: function () {
        return this.getPublishingOption(Item.PUBLISH_OPTION.STOREFRONT);
    },
    isItemRepost : function () {
        return this.getPublishingOption(Item.PUBLISH_OPTION.REPOST);
    },
    getNumImages: function () {
        var images = this.get('images');
        var count = 0;

        images.each(function (image) {
            if (image.get('large') && image.get('large') !== '') {
                count += 1;
            }
        });

        return count;
    },
    /**
     * @deprecated
     */
    getShardedImageUrl: function (index, size, imageHosts) {
        var image = this.get('images[' + index + '].' + size);
        if (image) {
            return shard.getUrl(image, imageHosts);
        }
    },
    /**
     * @param options
     * @param [options.position=1] {number} the image position
     * @param [options.size=small] {string} the image size
     * @return {string|undefined}
     */
    getImageUrl: function (options) {
        var images = this.get('images');
        var image;

        options = options || {};
        options.position = options.position || 1;
        options.size = options.size || 'small';

        image = images && images.findWhere({position: options.position});
        if (!image) {
            return;
        }
        return image.get(options.size);  // eslint-disable-line consistent-return
    },
    supports21stCenturyData: function () {
        return this.isFurniture();
    },
    getPeriod: function () {
        return this.get('attributes.period[0].value');
    },
    getCreator: function () {
        return this.get('creators[0].creator.name');
    },

    /**
     * Returns array of creator names.
     * @return {Array} array of strings
     */
    getCreators: function () {
        var creators = this.get('creators');
        var creatorDisplayNames = [];
        var creatorIdentifierField;

        if (this.isFashion() || this.isJewelry()) {
            creatorIdentifierField = 'name';
        } else {
            creatorIdentifierField = 'displayName';
        }

        // check if the creators collection is defined, and if so,
        // send the creator display names (if any) to the template
        if (creators) {
            // iterate over the creators collection
            creators.each(function (creatorModel) {
                var displayName = creatorModel.get('creator.' + creatorIdentifierField);
                if (!_.isUndefined(displayName) && !_.contains(creatorDisplayNames, displayName)) {
                    creatorDisplayNames.push(displayName);
                }
            });
        }

        return creatorDisplayNames;
    },

    getLargeImages: function () {
        var images = this.get('images') || [];
        return images.filter(function (image) {
            return image.get('large');
        });
    },

    getCategory: function (level) {
        var categories = this.get('classification.inflatedCategories');
        if (_.isArray(categories) && categories[0] && categories[0][level - 1]) {
            return categories[0][level - 1].label;
        }
    },

    /**
     * @returns {ItemCategories}
     */
    getItemCategories : function () {
        return this.get('classification.categories');
    },

    /**
     * Return the primary item category, if one exists
     * @returns {ItemCategory}
     */
    getPrimaryItemCategory: function () {
        var itemCategories = this.getItemCategories();
        if (itemCategories) {
            return itemCategories.getPrimary();
        }
        return null;
    },

    /**
     * @param {string} itemCategoryKey
     * @returns {ItemCategory}
     */
    findItemCategoryByKey: function (itemCategoryKey) {
        var itemCategories = this.getItemCategories();
        if (itemCategories) {
            return itemCategories.findByCategoryKey(itemCategoryKey);
        }
        return null;
    },

    _ensureHasItemCategories : function () {
        var itemCategories = this.getItemCategories();
        if (itemCategories) {
            return itemCategories;
        }
        var classification = this.get('classification');
        if (!classification) {
            this.set('classification', { categories: [] });
        } else {
            classification.set('categories', []);
        }
        return this.getItemCategories();
    },

    /**
     * Add a new item category to the end
     * of the item categories collections.
     * @param {string} itemCategoryKey
     * @returns {Item}
     */
    addItemCategoryWithKey: function (itemCategoryKey) {
        var itemCategories = this.getItemCategories();
        if (!itemCategories) {
            itemCategories = this._ensureHasItemCategories();
        }
        itemCategories.addWithKey(itemCategoryKey);
        return this;
    },

    /**
     * Creates ItemCategories models based on the keys
     * passed in, and in the same order.
     * @param {string[]} keys
     * @returns {Item}
     */
    setItemCategoriesWithKeys: function (keys) {
        var itemCategories = this._ensureHasItemCategories();
        itemCategories.resetWithKeys(keys);
        return this;
    },

    /**
     * Searches the full vertical {@link Category} singleton
     * for the model that matches the key of the primary
     * {@link ItemCategory} for this item.
     * @returns {Category}
     */
    findPrimaryCategoryInVerticalCategory : function () {
        var primaryItemCategory = this.getPrimaryItemCategory();
        var categoryKey;
        var vertical;
        var verticalCategory;
        if (!primaryItemCategory) {
            return null;
        }
        categoryKey = primaryItemCategory.getCategoryKey();
        vertical = this.get('vertical');
        if (!vertical) {
            return null;
        }
        verticalCategory = Category.verticalCategoryInstance(vertical);
        return verticalCategory.findChildByKey(categoryKey);
    },

    getCategories: function () {
        var inflatedCategories = this.get('classification.inflatedCategories');
        return (inflatedCategories) ? inflatedCategories[0] : [];
    },

    /**
     * Category URL
     * @returns {string}
     */
    getCategoryURL: function () {
        var categories = this.getCategories();
        var parts = _.pluck(categories, 'label');
        if (parts.length) {
            return '/' + parts.join('/') + '/';
        }
        return '/';
    },

    /**
     * Get name of most specific category
     * @returns {string}
     */
    getLowestCategory: function () {
        var categories = this.getCategories();
        if (categories.length) {
            return (categories[categories.length - 1]).name;
        }
        return '';
    },
    /**
     * If the model is not published and the publish properties are 'Y', then
     * that means that this is a new item to publish.
     * @returns {boolean}
     */
    isNewPosting: function () {
        return (!this.isItemPublished() && this.testPublish());
    },
    //Publish options helper methods
    /**
     * Check if the item is deleted
     * @returns {boolean}
     */
    isItemDeleted: function () {
        return this.getPublishingOption(Item.PUBLISH_OPTION.DELETED);
    },
    /**
     * Check if the item is NYDC
     * @returns {boolean}
     */
    isItemNYDC: function () {
        return this.getPublishingOption(Item.PUBLISH_OPTION.NYDC);
    },
    /**
     * @param {string} value Either 'Y' or 'N' to indicate NYDC status
     * @param {Object} options
     * @returns {Promise}
     * @private
     */
    _updateNYDCStatus: function (value, options) {
        var self = this;
        return this.save(null, _.extend({
            url: endpoints.toggleNYDC(this.get('id')),
            type: 'PUT',
            patch: true,
            data: {
                isNydc: value
            }
        }, options)).done(function () {
            value = value === 'Y';
            self.setPublishingOption(Item.PUBLISH_OPTION.NYDC, value);
        });
    },
    /**
     * Add item to NYDC
     * @param {Object} options
     * @returns {Promise}
     */
    addToNYDC: function (options) {
        this._updateNYDCStatus('Y', options);
    },
    /**
     * Remove item from NYDC
     * @param {Object} options
     * @returns {Promise}
     */
    removeFromNYDC: function (options) {
        this._updateNYDCStatus('N', options);
    },
    /**
     * Check if the item is on hold
     * @returns {boolean}
     * @deprecated
     */
    isItemOnHold: function () {
        return this.getPublishingOption(Item.PUBLISH_OPTION.ON_HOLD);
    },
    /**
     * Check if the item is on hold
     * API parity with Item-v3 model
     * @returns {boolean}
     */
    isOnHold: function () {
        return this.isItemOnHold();
    },
    /**
     * Check if the item is sold
     * @returns {boolean}
     * @deprecated
     */
    isItemSold: function () {
        return this.getPublishingOption(Item.PUBLISH_OPTION.SOLD);
    },
    /**
     * Check if the item is sold
     * API parity with Item-v3 model
     * @returns {boolean}
     */
    isSold: function () {
        return this.isItemSold();
    },
    /**
     * Check if the item is unpublished
     * @returns {boolean}
     */
    isItemUnlisted: function () {
        return this.getPublishingOption(Item.PUBLISH_OPTION.UNPUBLISHED);
    },
    /**
     * Check if the item is Work In Progress
     * @returns {boolean}
     */
    isItemWorkInProgress: function () {
        return this.getPublishingOption(Item.PUBLISH_OPTION.WIP);
    },
    /**
     * Check if the item is featured
     * @returns {boolean}
     */
    isItemFeatured: function () {
        return this.getPublishingOption(Item.PUBLISH_OPTION.FEATURED);
    },
    /**
     * Check if the item is published
     * @returns {boolean}
     */
    isItemPublished: function () {
        return this.testPublish(this.get('publishOptions').toJSON());
    },

    /**
     * Check if the item is queued for Posting
     * @returns {boolean}
     */
    isItemQueuedForPosting: function () {
        return this.getPublishingOption(Item.PUBLISH_OPTION.QUEUED_FOR_POSTING);
    },

    /**
     * Check if the item will be marked as unavailable
     * @return {boolean}
     */
    isItemUnavailable: function () {
        return this.isItemDeleted() ||
            this.isItemUnlisted() ||
            this.dealerClosed() ||
            this.dealerPending() ||
            this.dealerSuspended();
    },

    /**
     * Add item to mailer
     * @returns {Deferred}
     */
    addMailer: function (options) {
        var mailer = new Mailer(null, { itemId: this.id });
        this.set('mailer', mailer);
        return mailer.add(options);
    },

    /**
     * Remove item from mailer
     * @returns {Deferred}
     */
    removeMailer: function (options) {
        var mailer = new Mailer(null, { itemId: this.id });
        this.set('mailer', mailer);
        return mailer.remove(options);
    },

    isInMailer: function () {
        return isYes(this.get('mailer.inMailer'));
    },

    /**
     * Saves storefront preferences to show the item on the storefront after it is sold
     * @param  {Object}
     * @return {Deferred}
     */
    showOnStorefrontWhenSold: function (options) {
        var storefrontPreferences = new StorefrontPreferences({}, { itemId: this.id });
        this.set('storefrontPreferences', storefrontPreferences);
        return storefrontPreferences.showWhenSold(options);
    },

    /**
     * Saves storefront preferences to hide the item on the storefront after it is sold
     * @param  {Object}
     * @return {Deferred}
     */
    hideOnStorefrontWhenSold: function (options) {
        var storefrontPreferences = new StorefrontPreferences({}, { itemId: this.id });
        this.set('storefrontPreferences', storefrontPreferences);
        return storefrontPreferences.hideWhenSold(options);
    },

    /**
     * Indicates if item is shown on storefront after sold
     * @return {Boolean}
     */
    isOnStorefrontWhenSold: function () {
        return isYes(this.get('storefrontPreferences.showOnStorefrontWhenSold'));
    },

    urlRoot: endpoints.inventoryRoot(),

    url: function urlItemModel() {
        // url is composed of the urlRoot, the vertical name, 'item', and the iid.
        var subStr = this.id ? this.id.toString().substr(0, 2) : null;
        var prefix = verticals.getVertical(subStr) || '';
        var vertical = this.get('vertical') || verticals.getVertical(prefix);
        var path = (vertical ? vertical + '/' : '') + 'item/' + (prefix ? this.id.substr(2) : this.id || '');
        return [this.urlRoot, path].join('/');
    },

    /**
     * Returns true if one of the publishOptions other than workInProgress, addText,
     * listedFrenchItem, unpublished or previouslySold is 'Y'
     * @returns {boolean}
     */
    testPublish : function (data) {
        var opts = data || (this.attributes.publishOptions ? this.get('publishOptions').attributes : null);
        if (!opts) {
            return undefined;
        }
        opts = _.omit(opts, ['workInProgress', 'addText', 'listedFrenchItem', 'unpublished', 'previouslySold']);
        return _.some(opts, function (val) {
            return isYes(val);
        });
    },

    /**
     * Change the id prefix of the item (if you are switching the item type.
     * @param newVertical {string} Name of the new vertical (e.g. fashion)
     * @returns {bunsen.Item}
     */
    changeIdPref: function (newVertical) {
        var id = this.get('id');
        var pref;
        var newPref;
        if (!id) {
            return this;
        }
        pref = id.substring(0, 2) || null;
        newPref = verticals.getPrefix(newVertical || this.get('vertical'));
        if (!verticals.getVertical(pref)) {
            return this.set('id', newPref + id, { silent: true });
        }
        return this.set('id', newPref + id.substring(2), { silent : true });
    },

    /**
     * Unsell an item
     * @param options - jQuery ajax options
     * @returns {ajaxPromise}
     */
    unsell: function (options) {
        return this.save(null, _.extend({}, options, {
            url: endpoints.unsellItem(this),
            type: 'PATCH'
        }));
    },

    /**
     * Put item on hold
     * @param options - jQuery ajax options
     * @returns {ajaxPromise}
     */
    hold: function (options) {
        return this.save(null, _.extend({}, options, {
            url:  endpoints.holdItem(this),
            type: 'PATCH'
        }));
    },

    /**
     * Mark item as deleted
     * @param options - Backbone.js options
     * @returns {ajaxPromise}
     */
    markDeleted: function (options) {
        return this.save(null, _.extend({}, options, {
            url:  endpoints.markDeleted(this.id),
            type: 'PATCH'
        }));
    },

    moveItemToSold: function () {
        this.setOnePublishOption(Item.PUBLISH_OPTION.SOLD);
    },

    removeItemFromSold: function () {
        this.setPublishingOption(Item.PUBLISH_OPTION.SOLD, false);
    },

    moveItemToPending: function () {
        this.setOnePublishOption(Item.PUBLISH_OPTION.WIP);
    },

    removeItemFromWIP : function () {
        this.setPublishingOption(Item.PUBLISH_OPTION.WIP, false);
    },

    moveItemToUnpublished: function () {
        this.setOnePublishOption(Item.PUBLISH_OPTION.UNPUBLISHED);
    },

    removeItemFromUnpublished : function () {
        this.setPublishingOption(Item.PUBLISH_OPTION.UNPUBLISHED, false);
    },

    addStorefrontListing : function () {
        this.setPublishingOption(Item.PUBLISH_OPTION.STOREFRONT, true);
    },

    addStorefrontListingOnly : function () {
        this.setOnePublishOption(Item.PUBLISH_OPTION.STOREFRONT);
    },

    removeStorefrontListing : function () {
        this.setPublishingOption(Item.PUBLISH_OPTION.STOREFRONT, false);
    },

    addFeaturedListing : function () {
        this.setPublishingOption(Item.PUBLISH_OPTION.FEATURED, true);
    },

    removeFeaturedListing : function () {
        this.setPublishingOption(Item.PUBLISH_OPTION.FEATURED, false);
    },

    moveItemToRepost : function () {
        this.setPublishingOption(Item.PUBLISH_OPTION.REPOST, true);
    },

    addMarketplaceListing : function () {
        this.setPublishingOption(Item.PUBLISH_OPTION.MARKETPLACE, true);
    },

    removeMarketplaceListing : function () {
        this.setPublishingOption(Item.PUBLISH_OPTION.MARKETPLACE, false);
    },

    hasChangedListings : function () {
        this.get('publishOptions').changedSinceLastSync([Item.PUBLISH_OPTION.UNPUBLISHED]);
    },

    /**
     * Determines if this item was storefront only as of last sync
     * @returns {boolean}
     */
    wasOnlyStorefrontListing : function () {
        var savedAttrs = this.lastSyncAttributes();
        return isYes(savedAttrs.publishOptions.storefront) && !isYes(savedAttrs.publishOptions.dibs);
    },

    /**
     * Determines where this item is published to
     * @returns [*]
     */
    getListings : function () {
        return _.reduce(Item.PUBLISH_OPTION, function (memo, value) {
            if (this.model.getPublishingOption(value)) {
                memo.push(value);
            }
            return memo;
        }, []);
    },

    /**
     * Take item off hold
     * @param options - jQuery ajax options
     * @returns {ajaxPromise}
     */
    unhold: function (options) {
        return this.save(null, _.extend({}, options, {
            url: endpoints.unholdItem(this),
            type: 'PATCH'
        }));
    },

    /**
     * Save an item's shipment quotes. Item is required
     * to have an id and a seller object.
     * @param {object} [saveOptions]
     * @returns {$.Deferred.promise.xhr}
     */
    saveShipmentQuotes: function (saveOptions) {
        var url = endpoints.itemShipmentQuotes(this.id);
        var shipmentQuotesJson;
        var self = this;
        saveOptions = _.extend({}, {
            url: url
        }, saveOptions);
        self.get('shipmentQuotes').each(function (quote) {
            if (!quote.get('author')) {
                quote.set('author', {
                    role: Party.ROLE_SELLER,
                    id: self.get('seller.id')
                });
            }
            quote.set({
                type: 'INVENTORY_POSTING'
            });
        }, self);
        shipmentQuotesJson = this.get('shipmentQuotes').toJSON();
        saveOptions.attrs = { dataList: shipmentQuotesJson };
        return self.save(null, saveOptions);
    },

    /**
     * Use clump service to save item and item
     * shipment quotes in one call
     * @returns {$.Deferred.promise}
     */
    saveItemAndShipmentQuotes: function (itemSaveOptions, quotesSaveOptions, clump) {
        itemSaveOptions = itemSaveOptions ? _.clone(itemSaveOptions) : {};
        quotesSaveOptions = quotesSaveOptions ? _.clone(quotesSaveOptions) : {};

        var usingOwnClump = _.isUndefined(clump);
        clump = !usingOwnClump ? clump : new Clump();

        itemSaveOptions.clump = !_.isUndefined(itemSaveOptions.clump) ?
            itemSaveOptions.clump : clump;
        quotesSaveOptions.clump = !_.isUndefined(quotesSaveOptions.clump) ?
            quotesSaveOptions.clump : clump;

        // todo: james b to move these references to the appropriate place in this scope (ie. to the top)
        var self = this;
        var dfd = $.Deferred();

        var quotesSave = this.saveShipmentQuotes(quotesSaveOptions).fail(function (xhr) {
            self.trigger('clumpSaveFailure:shipmentQuotes', xhr);
        });

        var itemSave = this.save(null, itemSaveOptions).fail(function (xhr) {
            self.trigger('clumpSaveFailure:item', xhr);
        });

        $.when(quotesSave, itemSave).done(function () {
            dfd.resolve.apply(dfd, arguments);
        }).fail(function () {
            dfd.reject.apply(dfd, arguments);
        });

        if (usingOwnClump) {
            clump.run({ abortOnError: true });
        }
        return dfd.promise();
    },

    /**
     * Return a PDP url path (e.g. /id-f_1234/ or /furniture/storage-case-pieces/credenzas/id-f_1234/)
     * @param {boolean} isIncludeCategories
     * @returns {string}
     */
    getPDPURL: function (isIncludeCategories) {
        return (isIncludeCategories ? this.getCategoryURL().slice(0, -1) : '') + '/id-' + this.id + '/';
    },

    getPdpUrls : function () {
        return this.get('pdpUrls') || [];
    },

    /**
     * @param {number} nth
     * @returns {string|null}
     */
    getNthPdpUrl : function (nth) {
        var urls = this.getPdpUrls();
        var url = null;
        nth = nth || 0;

        if (urls.length && urls[nth]) {
            url = urls[nth];
        }
        return url;
    },

    isSoldIndividually: function () {
        return this.get('pricing') && isYes(this.get('pricing.pricePerPiece'));
    },
    /**
     * fetches all available options for this item. Does not take shipment address
     * into consideration
     * @returns {*}
     */
    fetchAvailableOptions: function () {
        var availableOptions = new ShipmentQuotes();

        // Cancel any outstanding requests
        if (this._availableOptionsDeferred) {
            this._availableOptionsDeferred.reject();
        }

        this._availableOptionsDeferred = new $.Deferred();
        availableOptions.fetchAvailableOptions(this).done(_.bind(function () {
            this.availableOptions = availableOptions;
            this.selectFirstAvailableOption();
            this._availableOptionsDeferred.resolve(availableOptions);
        }, this)).fail(_.bind(function () {
            this._availableOptionsDeferred.reject();
        }, this));
        return this._availableOptionsDeferred.promise();
    },
    /**
     * Returns a promise that resolves with the available options.
     * Only fetches if they have not already been fetched
     */
    getAvailableOptionsPromise: function () {
        if (this._availableOptionsDeferred && this._availableOptionsDeferred.promise()) {
            return this._availableOptionsDeferred.promise();
        } else {
            return this.fetchAvailableOptions();
        }
    },

    /**
     * Re-order an item's images -- moving the image from the "fromPos" to the "toPos" -- works by manipulating the
     * "position" attribute of the item image sub-model
     * @param fromPos
     * @param toPos
     */
    reOrderImages : function (fromPos, toPos) {
        var img1 = this.get('images').findWhere({position : fromPos}).get('large');
        var img2 = this.get('images').findWhere({position : toPos}).get('large');
        assert((img1 && img2), 'Item does not have sufficient images to re-oreder');

        this.get('images').findWhere({position : fromPos}).set('position', toPos);
        this.get('images').findWhere({position : toPos}).set('position', fromPos);
    },

    /**
     * Return a specified publishOption
     * @param option
     * @returns boolean
     */
    getPublishingOption : function (option) {
        if (_.contains(Item.PUBLISH_OPTION, option)) {
            return isYes(this.get('publishOptions.' + option));
        }
        console.log("Error: Invalid publish option", option);
    },

    /**
     * Sets a publish option to a given value
     * @param option
     * @param bool
     */
    setPublishingOption : function (option, bool) {
        var value = bool ? 'Y' : 'N';
        if (!this.get('publishOptions')) {
            this.set('publishOptions', {});
        }
        if (_.contains(Item.PUBLISH_OPTION, option)) {
            this.set('publishOptions.' + option, value);
        } else {
            console.log('Error: Invalid publish option', option);
        }
    },

    /**
     * Sets one or more publishing options
     * @param optionsObject
     */
    setPublishingOptions : function (optionsObject) {
        _.each(optionsObject, function (value, key) {
            this.setPublishingOption(key, value);
        }, this);
    }

}, transitionMixin);


// Static functions
_.extend(Item, {
    //For ItemBaseView.js validation
    noValidateCondition : 'change:publishOptions',
    displayStatuses: {
        DELETED : "Deleted",
        NEW : "Add Text",
        ON_HOLD : "On Hold",
        PUBLISHED_TO_CATEGORY_SEARCH : "Published - Marketplace",
        PUBLISHED_TO_NEW_LISTINGS : "Published - Featured",
        QUEUED_FOR_CATEGORY_SEARCH : "Queued - Marketplace",
        QUEUED_FOR_NEW_LISTINGS : "Queued - Featured",
        SOLD : "Sold",
        STOREFRONT_ONLY : "Storefront Only",
        SUBMITTED_TO_CATEGORY_SEARCH : "Submitted - Marketplace",
        SUBMITTED_TO_NEW_LISTINGS : "Submitted - Featured",
        UNPUBLISHED : "Unlisted",
        WORK_IN_PROGRESS : "Work in Progress",
        REJECTED: "Action Required"
    },
    statuses: {
        DELETED : "DELETED",
        NEW : "NEW",
        ON_HOLD : "ON_HOLD",
        PUBLISHED_TO_CATEGORY_SEARCH : "PUBLISHED_TO_CATEGORY_SEARCH",
        PUBLISHED_TO_NEW_LISTINGS : "PUBLISHED_TO_NEW_LISTINGS",
        QUEUED_FOR_CATEGORY_SEARCH : "QUEUED_FOR_CATEGORY_SEARCH",
        QUEUED_FOR_NEW_LISTINGS : "QUEUED_FOR_NEW_LISTINGS",
        SOLD : "SOLD",
        STOREFRONT_ONLY : "STOREFRONT_ONLY",
        SUBMITTED_TO_CATEGORY_SEARCH : "SUBMITTED_TO_CATEGORY_SEARCH",
        SUBMITTED_TO_NEW_LISTINGS : "SUBMITTED_TO_NEW_LISTINGS",
        UNPUBLISHED : "UNPUBLISHED",
        WORK_IN_PROGRESS : "WORK_IN_PROGRESS",
        REJECTED: "REJECTED"
    },
    publishOptions: [
        "dibs", "featured", "nydc", "storefront", "workInProgress", "listedFrenchItem",
        "addText", "queuedForPosting", "rejected", "approved", "onHold", "deleted",
        "sold", "photoRepair", "previouslySold", "unpublished", "repost"
    ],
    PUBLISH_OPTION : {
        FEATURED : 'featured',
        MARKETPLACE : 'dibs',
        MARKET : 'marketplace',
        NYDC : 'nydc',
        STOREFRONT : 'storefront',
        WIP : 'workInProgress',
        ADD_TEXT : 'addText',
        REJECTED : 'rejected',
        APPROVED : 'approved',
        UNPUBLISHED : 'unpublished',
        REPOST : 'repost',
        SOLD : 'sold',
        DELETED : 'deleted',
        ON_HOLD : 'onHold',
        QUEUED_FOR_POSTING : 'queuedForPosting',
        PREVIOUSLY_SOLD: 'previouslySold',
        PHOTO_REPAIR : 'photoRepair',
        FRENCH_LISTING : 'listedFrenchItem'
    },
    IMAGES_API : {
        IMAGES_API_TYPE : 'images',
        IMAGES_ROTATION_API_TYPE : 'imageRotationRequest',
        IMAGES_ROTATION_TYPE : 'ROTATION'
    },
    defaultCollection : Items,
    getBaseDefaults: getDefaults,
    /**
     * @param {string} vertical
     * @returns {string}
     */
    getApiType: function (vertical) {
        return vertical + 'Item';
    },
    artDisplayStatuses: {
        PUBLISHED_TO_CATEGORY_SEARCH : "Published",
        PUBLISHED_TO_NEW_LISTINGS : "Published"
    }
});



//////////////////
// WEBPACK FOOTER
// ./~/bunsen/models/inventory/Item.js
// module id = 34
// module chunks = 62